import Image from 'next/image';
import React from 'react';
import { MarketingTeacher } from '../../../domain/teacher';
import { ExternalLink } from '../../common/externalLink';
import Spotify from '../../icons/spotify';
import HeroBannerCarousel from './heroBannerCarousel';

interface HeroBannerProps {
  teacher: MarketingTeacher;
}

const HeroBanner: React.FC<HeroBannerProps> = ({ teacher }) => {
  const { bannerImage, name, position } = teacher;

  const socialLinks = [
    { link: teacher.website, svg: '/svg/web.svg', alt: 'Website' },
    {
      link: teacher.instagram,
      svg: '/svg/instagram.svg',
      alt: 'Instagram',
    },
    { link: teacher.facebook, svg: '/svg/facebook.svg', alt: 'Facebook' },
    { link: teacher.spotifyProfile, icon: Spotify, alt: 'Spotify' },
  ];

  // array of images with duplicates removed
  const images = [
    bannerImage,
    ...(teacher.photosCollection?.items || []),
  ].filter(
    (image, index, arr) =>
      arr.findIndex(i => i.sys.id === image.sys.id) === index
  );

  return (
    <section className="relative flex items-end text-white h-hero-full-with-sticky-page-nav">
      {images.length === 1 ? (
        <Image
          layout="fill"
          className="absolute object-cover w-full h-full"
          src={`${images[0]?.url}?w=1600&q=90`}
          alt={images[0].title}
          priority
        />
      ) : (
        <HeroBannerCarousel images={images} />
      )}

      <div className="absolute h-[208px] transform -translate-y-1/2 top-0 w-full bg-gradient-to-b from-background-dark to-transparent" />
      <div className="absolute bottom-0 w-full h-1/3 bg-gradient-to-t from-background-dark to-transparent" />

      <div className="relative flex flex-col items-center w-full mb-16">
        <h1 className="text-4xl md:text-5xl font-family-bold">{name}</h1>
        {position && <p className="pt-8px">{position}</p>}
        <div className="flex flex-row items-center justify-center gap-x-4 pt-16px">
          {socialLinks
            .filter(link => !!link.link)
            .map(link => (
              <ExternalLink
                key={link.link}
                href={link.link}
                className="flex items-center justify-center h-24px w-24px"
              >
                {link.icon && (
                  <link.icon classes="h-24px w-24px" aria-label={link.alt} />
                )}
                {link.svg && (
                  <Image
                    height={24}
                    width={24}
                    src={link.svg}
                    alt={link.alt}
                    aria-label={link.alt}
                  />
                )}
              </ExternalLink>
            ))}
        </div>
      </div>
    </section>
  );
};

export default HeroBanner;
